import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { AnimationClip } from 'three'
import { SkeletonType } from '../../lib/enums/SkeletonType'
import {
  type AnimationClipMetadata,
  type TransformedAnimationClipPair
} from '../anim-library/interfaces/TransformedAnimationClipPair'
import { UserClipStorage } from './UserClipStorage'
import { CustomSkeletonStorage } from './CustomSkeletonStorage'
import {
  parse_skeleton_descriptor,
  type SkeletonDescriptor
} from '../skeleton/SkeletonDescriptor'

/**
 * Export / import the user's "project" — bundle of user-authored clips and
 * mocap settings — as a single `.meshinator.json` file.
 *
 * Scope: clips per skeleton type + mocap settings (disabled bones, root-motion
 * flag, provider id). The base GLB model is NOT included in v1 — re-loading the
 * model triggers the multi-step model-load flow which is too tightly coupled
 * to the engine to invoke programmatically. Users re-load their model and then
 * import the project file to get their clips back.
 *
 * The format is a single JSON document so it's diffable and inspectable. If we
 * later add binary assets (model bytes, thumbnails as separate files), we'll
 * switch to a `.meshinator.zip` and migrate the version field.
 */

const FORMAT_TAG = 'meshinator-project'
const FORMAT_VERSION = 1
/** v2 bundles a mesh GLB + skeleton descriptor alongside the JSON manifest in
 *  a `.meshinator.zip`. Produced today only by the MCP server (meshinator-mcp);
 *  the browser exporter still writes v1 JSON. */
const FORMAT_VERSION_V2 = 2

/** Filenames the v2 zip layout uses for its bundled artifacts. The manifest's
 *  `mesh.filename` and `active_skeleton.filename` fields point at these. */
export const V2_MANIFEST_FILENAME = 'project.json'
export const V2_MESH_FILENAME = 'mesh.glb'
export const V2_SKELETON_FILENAME = 'skeleton.json'

const SETTINGS_KEYS = [
  'meshinator-mocap-disabled-bones',
  'meshinator-mocap-root-motion-enabled',
  'meshinator-mocap-provider',
  'meshinator-mocap-preview-position',
  'meshinator-welcome-seen'
]

interface SerializedClipPair {
  display_clip: ReturnType<typeof AnimationClip.toJSON>
  original_clip: ReturnType<typeof AnimationClip.toJSON>
  metadata: AnimationClipMetadata
}

interface ProjectFileV1 {
  format: typeof FORMAT_TAG
  version: 1
  exported_at: string
  skeletons: Record<string, { clips: SerializedClipPair[] }>
  settings: Record<string, string>
  /** User-authored skeletons from Build Skeleton mode. Optional for back-compat
   *  with archives produced before this field existed. */
  custom_skeletons?: SkeletonDescriptor[]
}

export interface ProjectFileV2 {
  format: typeof FORMAT_TAG
  version: 2
  exported_at: string
  skeletons: Record<string, { clips: SerializedClipPair[] }>
  settings: Record<string, string>
  custom_skeletons?: SkeletonDescriptor[]
  /** Pointer to the rigged mesh GLB inside the zip. */
  mesh?: { filename: string }
  /** Pointer to the skeleton descriptor JSON inside the zip + which custom
   *  skeleton id it corresponds to. */
  active_skeleton?: { filename: string, skeleton_id: string }
}

export interface ParsedV2Zip {
  manifest: ProjectFileV2
  mesh_bytes: Uint8Array | null
  descriptor: SkeletonDescriptor | null
}

export class ProjectArchive {
  /**
   * Build the project archive from the current state and trigger a download.
   * `loaded_clips` is the in-memory active-skeleton list (for the in-flight
   * skeleton). All other skeleton types are pulled from IDB.
   */
  static async download (active_skeleton: SkeletonType, loaded_clips: TransformedAnimationClipPair[]): Promise<void> {
    const skeletons: ProjectFileV1['skeletons'] = {}

    // Active skeleton: use the in-memory list (it might have unsaved changes
    // since the IDB write is debounced).
    skeletons[active_skeleton] = {
      clips: loaded_clips
        .filter(p => p.metadata.source_type === 'user-authored')
        .map(serialize_pair)
    }

    // All other skeletons: pull from IDB so a project file holds *all* the
    // user's clips, not just the currently-loaded skeleton's.
    for (const type of Object.values(SkeletonType)) {
      if (type === active_skeleton) continue
      if (type === SkeletonType.Error || type === SkeletonType.None) continue
      const saved = await UserClipStorage.load(type as SkeletonType)
      if (saved.length > 0) {
        skeletons[type] = { clips: saved.map(serialize_pair) }
      }
    }

    const settings: Record<string, string> = {}
    for (const key of SETTINGS_KEYS) {
      const value = localStorage.getItem(key)
      if (value !== null) settings[key] = value
    }

    const custom_skeletons = await CustomSkeletonStorage.list()

    const file: ProjectFileV1 = {
      format: FORMAT_TAG,
      version: FORMAT_VERSION,
      exported_at: new Date().toISOString(),
      skeletons,
      settings,
      custom_skeletons
    }

    const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' })
    const filename = `meshinator-project-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.meshinator.json`
    saveAs(blob, filename)
  }

  /**
   * Parse and validate a v1 project file. Throws a user-readable Error if the
   * file isn't a recognized v1 project archive.
   */
  static parse (json_text: string): ProjectFileV1 {
    let parsed: unknown
    try {
      parsed = JSON.parse(json_text)
    } catch (_err) {
      throw new Error('That file is not valid JSON.')
    }
    if (typeof parsed !== 'object' || parsed === null) {
      throw new Error('That file is not a Meshinator project (root must be an object).')
    }
    const candidate = parsed as Partial<ProjectFileV1>
    if (candidate.format !== FORMAT_TAG) {
      throw new Error('That file is not a Meshinator project (missing format tag).')
    }
    if (candidate.version !== FORMAT_VERSION) {
      throw new Error(`Unsupported project version: ${String(candidate.version)} (expected ${FORMAT_VERSION}).`)
    }
    if (typeof candidate.skeletons !== 'object' || candidate.skeletons === null) {
      throw new Error('Project file is missing the skeletons section.')
    }
    if (typeof candidate.settings !== 'object' || candidate.settings === null) {
      throw new Error('Project file is missing the settings section.')
    }
    return candidate as ProjectFileV1
  }

  /**
   * Pure-JSON parser for a v2 manifest. Same shape validation as v1 plus the
   * optional `mesh` / `active_skeleton` pointers. Does not read any bundled
   * binary — `parse_zip` is the entry point for full v2 archives.
   */
  static parse_v2_manifest_json (json_text: string): ProjectFileV2 {
    let parsed: unknown
    try {
      parsed = JSON.parse(json_text)
    } catch (_err) {
      throw new Error('Project manifest is not valid JSON.')
    }
    if (typeof parsed !== 'object' || parsed === null) {
      throw new Error('Project manifest must be a JSON object.')
    }
    const candidate = parsed as Partial<ProjectFileV2>
    if (candidate.format !== FORMAT_TAG) {
      throw new Error('Project manifest is missing the format tag.')
    }
    if (candidate.version !== FORMAT_VERSION_V2) {
      throw new Error(`Unsupported v2 manifest version: ${String(candidate.version)}.`)
    }
    if (typeof candidate.skeletons !== 'object' || candidate.skeletons === null) {
      throw new Error('Project manifest is missing the skeletons section.')
    }
    if (typeof candidate.settings !== 'object' || candidate.settings === null) {
      throw new Error('Project manifest is missing the settings section.')
    }
    if (candidate.mesh !== undefined) {
      if (typeof candidate.mesh.filename !== 'string') {
        throw new Error('Project manifest mesh.filename must be a string.')
      }
    }
    if (candidate.active_skeleton !== undefined) {
      if (typeof candidate.active_skeleton.filename !== 'string' || typeof candidate.active_skeleton.skeleton_id !== 'string') {
        throw new Error('Project manifest active_skeleton needs filename and skeleton_id strings.')
      }
    }
    return candidate as ProjectFileV2
  }

  /**
   * Read a `.meshinator.zip` v2 archive. Returns the parsed manifest plus the
   * embedded mesh GLB bytes and skeleton descriptor when present. Accepts the
   * raw zip bytes (Uint8Array, Blob, or ArrayBuffer).
   */
  static async parse_zip (input: Uint8Array | ArrayBuffer | Blob): Promise<ParsedV2Zip> {
    const zip = await JSZip.loadAsync(input)
    const manifest_file = zip.file(V2_MANIFEST_FILENAME)
    if (manifest_file === null) {
      throw new Error(`Zip is missing ${V2_MANIFEST_FILENAME}; not a v2 project archive.`)
    }
    const manifest_text = await manifest_file.async('string')
    const manifest = ProjectArchive.parse_v2_manifest_json(manifest_text)

    let mesh_bytes: Uint8Array | null = null
    if (manifest.mesh !== undefined) {
      const mesh_file = zip.file(manifest.mesh.filename)
      if (mesh_file !== null) mesh_bytes = await mesh_file.async('uint8array')
    }

    let descriptor: SkeletonDescriptor | null = null
    if (manifest.active_skeleton !== undefined) {
      const skel_file = zip.file(manifest.active_skeleton.filename)
      if (skel_file !== null) {
        const skel_text = await skel_file.async('string')
        descriptor = parse_skeleton_descriptor(JSON.parse(skel_text))
      }
    }

    return { manifest, mesh_bytes, descriptor }
  }

  /**
   * Apply a parsed project file: replace IDB clips for each skeleton type and
   * write settings to localStorage. Returns a summary of what was imported.
   *
   * The active skeleton's in-memory clip list is NOT touched here — the caller
   * is responsible for reloading it from IDB after this returns (via the
   * existing `restore_user_clips_async` flow on next skeleton load, or an
   * explicit reload).
   */
  static async apply (project: ProjectFileV1): Promise<{ skeleton_clip_counts: Record<string, number>, settings_applied: number, custom_skeletons_imported: number }> {
    const skeleton_clip_counts: Record<string, number> = {}

    await UserClipStorage.clear_all()
    for (const [skeleton_key, entry] of Object.entries(project.skeletons)) {
      const skeleton_type = skeleton_key as SkeletonType
      const pairs: TransformedAnimationClipPair[] = entry.clips.map(deserialize_pair)
      await UserClipStorage.save(skeleton_type, pairs)
      skeleton_clip_counts[skeleton_key] = pairs.length
    }

    let settings_applied = 0
    for (const [key, value] of Object.entries(project.settings)) {
      // Allow-list to defend against a malicious project file dumping arbitrary
      // localStorage entries (e.g. for cross-app interference).
      if (!SETTINGS_KEYS.includes(key)) continue
      try {
        localStorage.setItem(key, value)
        settings_applied++
      } catch (_) { /* quota / private mode — non-fatal */ }
    }

    let custom_skeletons_imported = 0
    if (project.custom_skeletons !== undefined) {
      for (const raw of project.custom_skeletons) {
        try {
          const descriptor = parse_skeleton_descriptor(raw)
          const saved = await CustomSkeletonStorage.put(descriptor)
          if (saved) custom_skeletons_imported++
        } catch (err) {
          console.warn('Skipping invalid custom skeleton in archive:', err)
        }
      }
    }

    return { skeleton_clip_counts, settings_applied, custom_skeletons_imported }
  }
}

function serialize_pair (pair: TransformedAnimationClipPair): SerializedClipPair {
  return {
    display_clip: AnimationClip.toJSON(pair.display_animation_clip),
    original_clip: AnimationClip.toJSON(pair.original_animation_clip),
    metadata: { ...pair.metadata }
  }
}

function deserialize_pair (rec: SerializedClipPair): TransformedAnimationClipPair {
  return {
    display_animation_clip: AnimationClip.parse(rec.display_clip),
    original_animation_clip: AnimationClip.parse(rec.original_clip),
    metadata: rec.metadata
  }
}
